// Result Widgets - Display simulation results

import { Trophy, Info } from "lucide-react";
import { formatCurrency, getScoreColorClass } from "./helpers";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const n2 = (v) => Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 2 });

// ⓘ com a conta usada na Receita Anual (rate → mês → × meses faturados)
const RevenueInfo = ({ result, formData }) => {
  const isFix = !!formData?.is_fix_fee && Number(formData?.fix_fee_value) > 0;
  const type = isFix ? (formData?.fix_fee_type || "monthly") : (formData?.client_rate_type || "hourly");
  const hours = result.hours_per_month_used || Number(formData?.hours_per_month) || 168;
  const hpd = Number(formData?.hours_per_day) || 8;
  const months = result.billable_months;
  const monthLine =
    type === "hourly" ? `${formatCurrency(result.rate_hourly)}/h × ${n2(hours)}h (${n2(hpd)}h/dia × ${n2(hours / hpd)} dias úteis médios) = ${formatCurrency(result.rate_monthly)}/mês`
    : type === "daily" ? `${formatCurrency(result.rate_daily)}/dia × ${n2(result.rate_monthly / (result.rate_daily || 1))} dias = ${formatCurrency(result.rate_monthly)}/mês`
    : `${formatCurrency(result.rate_monthly)}/mês`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="text-emerald-600 hover:text-emerald-800" data-testid="annual-revenue-info-btn" aria-label="Como é calculada a Receita Anual">
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-80 text-xs space-y-1" data-testid="annual-revenue-info">
        <div className="font-semibold">Como é calculada a Receita Anual</div>
        {isFix && <div className="text-pink-700">Fix Fee{formData?.fix_fee_12_months !== false ? " — faturado 12 meses" : ""}</div>}
        <div>{monthLine}</div>
        <div>{formatCurrency(result.rate_monthly)} × {n2(months)} meses = <strong>{formatCurrency(result.annual_revenue)}</strong></div>
        <div className="text-slate-500">Receita mensal média: {formatCurrency(result.annual_revenue)} ÷ 12 = {formatCurrency(result.client_revenue)}</div>
      </PopoverContent>
    </Popover>
  );
};

// ============ BUSINESS SNAPSHOT (compact 4-KPI card: Receita | Custo | Margem | Fluidez) ============
export const BusinessSnapshotWidget = ({ result, formData, marginColors, snapshotColors }) => {
  if (!result) {
    return (
      <div className="p-3 h-full bg-slate-100 rounded-lg flex items-center justify-center text-xs text-muted-foreground" data-testid="business-snapshot-empty">
        Aguardando cálculo...
      </div>
    );
  }

  const isPerm = formData?.business_type === "perm";
  const revenue = isPerm ? (result.breakdown?.total_fee || result.client_revenue || 0) : (result.client_revenue || 0);
  const cost = result.total_company_cost || 0;
  const marginAmount = result.margin_amount || 0;
  const marginPct = result.margin_percentage || 0;
  const paymentTerms = formData?.payment_terms || 30;
  const fluidezLabel = paymentTerms <= 30 ? "Alta" : paymentTerms <= 60 ? "Média" : "Baixa";
  const mc = getMarginColor(marginPct, marginColors);
  const colors = snapshotColors || {};

  return (
    <div className="p-2 h-full flex flex-col gap-2" data-testid="business-snapshot-widget">
      {/* MARGEM (destaque principal, compacto) */}
      <div
        className={`px-3 py-2 rounded-lg flex items-center justify-between ${mc.danger ? 'border-2 border-red-500 animate-pulse' : ''}`}
        style={{ backgroundColor: mc.bg }}
        data-testid="snapshot-margem"
      >
        <div className="flex items-center gap-1.5">
          {mc.danger && <span aria-label="alerta" className="text-sm">⚠️</span>}
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: mc.text }}>
            Margem
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold font-mono" style={{ color: mc.text, opacity: 0.8 }}>
            {marginPct.toFixed(1)}%
          </span>
          <span className="text-base font-bold font-mono" style={{ color: mc.text }}>
            {formatCurrency(marginAmount)}
          </span>
        </div>
      </div>

      {/* KPIs de suporte: Receita | Custo | Fluidez */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div
          className="px-3 py-2 rounded-md flex items-center justify-between"
          style={{ backgroundColor: colors.receita_bg || '#dbeafe' }}
          data-testid="snapshot-receita"
        >
          <span className="font-semibold uppercase text-[10px] tracking-wide" style={{ color: colors.receita_text || '#1d4ed8' }}>
            Receita
          </span>
          <span className="font-bold font-mono" style={{ color: colors.receita_text || '#1d4ed8' }}>
            {formatCurrency(revenue)}
          </span>
        </div>
        <div
          className="px-3 py-2 rounded-md flex items-center justify-between"
          style={{ backgroundColor: colors.custo_bg || '#f3f4f6' }}
          data-testid="snapshot-custo"
        >
          <span className="font-semibold uppercase text-[10px] tracking-wide" style={{ color: colors.custo_text || '#374151' }}>
            Custo
          </span>
          <span className="font-bold font-mono" style={{ color: colors.custo_text || '#374151' }}>
            {formatCurrency(cost)}
          </span>
        </div>
        <div
          className="px-3 py-2 rounded-md flex items-center justify-between"
          style={{ backgroundColor: colors.fluidez_bg || '#f1f5f9' }}
          data-testid="snapshot-fluidez"
        >
          <span className="font-semibold uppercase text-[10px] tracking-wide" style={{ color: colors.fluidez_text || '#334155' }}>
            Fluidez
          </span>
          <span className="font-bold font-mono" style={{ color: colors.fluidez_text || '#334155' }}>
            {paymentTerms}d · {fluidezLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

// Helper: get margin color from configurable ranges
const getMarginColor = (marginPct, marginColors) => {
  // Margem negativa: sempre vermelho com alerta (regra de negócio)
  if (marginPct < 0) {
    return { bg: "#FEE2E2", text: "#B91C1C", danger: true };
  }
  const defaultRanges = [
    { min: 0, max: 5, bg: "#FEF2F2", text: "#DC2626" },
    { min: 5, max: 10, bg: "#FFF7ED", text: "#EA580C" },
    { min: 10, max: 20, bg: "#FEFCE8", text: "#CA8A04" },
    { min: 20, max: null, bg: "#F0FDF4", text: "#16A34A" },
  ];
  const ranges = marginColors?.ranges || defaultRanges;
  for (const r of ranges) {
    const inRange = r.max === null ? marginPct >= r.min : (marginPct >= r.min && marginPct < r.max);
    if (inRange) return { bg: r.bg, text: r.text, danger: false };
  }
  return { bg: "#F0FDF4", text: "#16A34A", danger: false };
};

// ============ SCORE ============
export const ScoreWidget = ({ result, results }) => {
  if (!result) {
    return (
      <div className="h-full bg-slate-100 rounded-lg flex items-center justify-center">
        <span className="text-sm text-muted-foreground">--</span>
      </div>
    );
  }

  const bd = results?.business_decision || {};
  // Somar TODOS os tipos de prémio monetário: prémio proposto pelo simulador,
  // bónus fixo (por escalão de margem) e prémio manual (admin override).
  // NOTA: manual_premium_value vem no ITEM `result` (não em `results` que é a
  // resposta completa) — bug anterior estava a ler do sitio errado.
  const premiumProposed = Number(bd?.premium?.premium_value) || 0;
  const bonusFixed = Number(bd?.fixed_bonus?.bonus_value) || 0;
  const manualPremium = Number(result?.manual_premium_value) || 0;
  const totalPremium = premiumProposed + bonusFixed + manualPremium;
  const hasPremium = totalPremium > 0;
  // Fee Perm fixo é uma % — mostramos como badge separado
  const permFeePct = bd?.fixed_perm_fee?.fee_percentage;
  const marginPct = result.margin_percentage || 0;
  // A Faixa e o Premio sao determinados pela margem BRUTA (antes de deduzir
  // o premio). Mostramos esse mesmo numero aqui para que o valor, a Faixa e
  // o Premio digam sempre respeito a mesma margem.
  const grossAmount = (result.gross_margin_amount != null)
    ? result.gross_margin_amount
    : (result.margin_amount || 0);
  const grossPct = (result.gross_margin_percentage != null)
    ? result.gross_margin_percentage
    : marginPct;
  const showAfterPremium = Math.abs(grossAmount - (result.margin_amount || 0)) > 0.005;
  const isNegative = marginPct < 0;

  return (
    <div className={`h-full rounded-lg p-2 ${isNegative ? 'bg-red-100 text-red-800 border-2 border-red-500 animate-pulse' : getScoreColorClass(result.score_color)}`} data-testid="score-widget">
      <div className="h-full flex flex-col items-center justify-center text-center overflow-hidden">
        {isNegative && <div className="text-sm font-bold" aria-label="alerta">⚠️ MARGEM NEGATIVA</div>}
        <div className="text-xl sm:text-2xl font-bold leading-none">{grossPct.toFixed(1)}%</div>
        <div className="text-xs sm:text-sm font-mono mt-0.5 opacity-90 leading-tight truncate w-full" title={formatCurrency(grossAmount)}>
          {formatCurrency(grossAmount)}
        </div>
        {showAfterPremium && (
          <div
            className="text-[10px] sm:text-xs opacity-75 leading-tight truncate w-full"
            data-testid="score-widget-after-premium"
            title="Margem depois de deduzir o prémio. Igual a Receita menos Custo Total."
          >
            Após prémio: {formatCurrency(result.margin_amount)}
          </div>
        )}
        {result.score_label && (
          <div
            className="text-[10px] sm:text-xs opacity-80 mt-0.5 truncate w-full"
            title={`Faixa/categoria da margem — configurável em /admin/margins. O nome descreve a saúde do negócio, não o prémio (o prémio aparece com o ícone 🏆 abaixo, se aplicável).`}
          >
            Faixa: {result.score_label}
          </div>
        )}
        {hasPremium && (
          <div
            className="mt-1 flex items-center gap-1 truncate w-full justify-center text-xs font-semibold"
            data-testid="score-widget-premium"
            title={`Prémio total: proposta ${formatCurrency(premiumProposed)} + bónus ${formatCurrency(bonusFixed)} + manual ${formatCurrency(manualPremium)}`}
          >
            <Trophy className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Prémio: {formatCurrency(totalPremium)}</span>
          </div>
        )}
        {permFeePct != null && (
          <div className="text-[10px] opacity-80 mt-0.5 truncate w-full" title="Fee Perm fixo por escalão">
            Fee {permFeePct}%
          </div>
        )}
      </div>
    </div>
  );
};

// ============ COMPANY RESULTS ============
export const CompanyResultsWidget = ({ result, results, formData, marginColors }) => {
  if (!result) {
    return (
      <div className="p-3 h-full bg-slate-100 rounded-lg flex items-center justify-center text-sm text-muted-foreground">
        Aguardando cálculo...
      </div>
    );
  }

  const isPerm = formData?.business_type === "perm";

  // ===== Vista Perm — colocação permanente (sem custos mensais da empresa) =====
  if (isPerm) {
    const bd = result.breakdown || {};
    const totalFee = bd.total_fee || result.client_revenue || 0;
    const opCost = bd.operational_cost || result.total_company_cost || 0;
    const feeBase = bd.fee_base || 0;
    const candidateSalary = bd.candidate_salary || 0;
    const benefitsAnnual = bd.perm_benefits_annual || 0;
    const percentageRate = bd.percentage_rate || 0;
    const fixedFee = bd.fixed_fee || 0;
    const mc = getMarginColor(result.margin_percentage || 0, marginColors);
    return (
      <div className="p-2 text-xs space-y-2 overflow-y-auto max-h-[400px]">
        <div className="text-xs font-semibold text-brand-600 uppercase mb-1">Detalhe Negócio Perm</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-blue-50 rounded text-center">
            <div className="text-[10px] text-blue-600">Receita (Fee)</div>
            <div className="font-bold text-sm text-blue-700">{formatCurrency(totalFee)}</div>
          </div>
          <div className="p-2 bg-orange-50 rounded text-center">
            <div className="text-[10px] text-orange-600">Custo Operacional</div>
            <div className="font-bold text-sm text-orange-700">{formatCurrency(opCost)}</div>
          </div>
        </div>
        <div className="pt-2 border-t border-slate-200">
          <div className="text-[10px] font-semibold text-blue-600 uppercase mb-1">Base de Cálculo do Fee</div>
          <div className="flex justify-between p-1 bg-slate-50 rounded">
            <span>Salário Anual ({bd.perm_salary_months || 14} meses)</span>
            <span>{formatCurrency(candidateSalary)}</span>
          </div>
          {benefitsAnnual > 0 && (
            <div className="flex justify-between p-1 bg-slate-50 rounded">
              <span>+ Benefícios Anuais</span>
              <span>{formatCurrency(benefitsAnnual)}</span>
            </div>
          )}
          <div className="flex justify-between p-1 bg-blue-100 rounded font-semibold mt-1">
            <span>Base Total</span>
            <span className="text-blue-700">{formatCurrency(feeBase)}</span>
          </div>
          <div className="flex justify-between p-1 bg-blue-50 rounded text-[10px]">
            <span>Fee {percentageRate}%</span>
            <span className="text-blue-700">{formatCurrency(totalFee - fixedFee)}</span>
          </div>
          {fixedFee > 0 && (
            <div className="flex justify-between p-1 bg-blue-50 rounded text-[10px]">
              <span>+ Fee Fixo</span>
              <span className="text-blue-700">{formatCurrency(fixedFee)}</span>
            </div>
          )}
        </div>
        <div className="pt-2 border-t border-slate-200">
          <div className="text-[10px] font-semibold text-orange-600 uppercase mb-1">Único Custo da Empresa</div>
          <div className="flex justify-between p-1 bg-orange-50 rounded">
            <span>Custos Operacionais (10%)</span>
            <span className="text-orange-600">{formatCurrency(opCost)}</span>
          </div>
          <p className="text-[10px] text-slate-500 italic mt-1">
            Em Perm a empresa não suporta benefícios — os benefícios entram apenas na base do fee.
          </p>
        </div>
        <div className="pt-2 border-t border-slate-200">
          <div className="p-2 rounded text-center" style={{ backgroundColor: mc.bg }}>
            <div className="text-[10px]" style={{ color: mc.text }}>Margem</div>
            <div className="font-bold text-sm" style={{ color: mc.text }}>{formatCurrency(result.margin_amount)}</div>
            <div className="text-[10px]" style={{ color: mc.text, opacity: 0.8 }}>{result.margin_percentage?.toFixed(1)}%</div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Vista Outsourcing — original =====
  const paymentTerms = formData?.payment_terms || 30;
  const monthlyRevenue = result.client_revenue || 0;
  const monthlyCost = result.total_company_cost || 0;  const fluidezLabel = paymentTerms === 30 ? "Alta" : paymentTerms === 60 ? "Média" : "Baixa";
  const fluidezColor = paymentTerms === 30 ? "text-brand-600 bg-brand-50" : paymentTerms === 60 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";
  const costsBreakdown = result.costs_breakdown || [];
  // client_revenue já é média 12m (rate × meses faturáveis / 12) — usar o anual do backend
  const annualRevenue = result.annual_revenue ?? monthlyRevenue * 12;
  // Usa total_company_cost_annual do backend (respeita meses pagos de cada benefício,
  // ex.: Sub. Alimentação × 11m). Fallback para × 12 se backend não expuser (retro-compat).
  const annualCost = result.total_company_cost_annual != null
    ? result.total_company_cost_annual
    : monthlyCost * 12;
  const annualMargin = annualRevenue - annualCost;
  
  return (
    <div className="p-2 text-xs space-y-2 overflow-y-auto max-h-[400px]">
      <div className="text-xs font-semibold text-brand-600 uppercase mb-1">Detalhe Empresa</div>
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 bg-emerald-50 rounded text-center border border-emerald-200">
          <div className="text-[10px] text-emerald-700">Receita</div>
          <div className="font-bold text-sm text-emerald-700">{formatCurrency(monthlyRevenue)}</div>
        </div>
        <div className="p-2 bg-red-50 rounded text-center border border-red-200">
          <div className="text-[10px] text-red-700">Custo Total</div>
          <div className="font-bold text-sm text-red-700">{formatCurrency(monthlyCost)}</div>
        </div>
      </div>
      <div className="pt-2 border-t border-slate-200">
        <div className="text-[10px] font-semibold text-brand-600 uppercase mb-1">Despesas Mensais</div>
        <div className="flex justify-between p-1 rounded">
          <span>Salário Bruto</span>
          <span>{formatCurrency(result.gross_monthly)}</span>
        </div>
        {(() => {
          // Indices dos 3 maiores custos para destacar
          const topIndices = costsBreakdown
            .map((c, i) => ({ i, v: c.value || 0 }))
            .sort((a, b) => b.v - a.v)
            .slice(0, 3)
            .filter((x) => x.v > 0)
            .map((x) => x.i);
          return costsBreakdown.map((cost, idx) => {
            const isAcidentes = cost.name && cost.name.toLowerCase().includes("acidente");
            const topRank = topIndices.indexOf(idx); // 0,1,2 ou -1
            return (
              <div
                key={`${cost.name}-${idx}`}
                className="flex justify-between items-center p-1 rounded"
                title={isAcidentes ? "Fórmula: (Salário Bruto Anual ×14 + Sub. Alimentação Anual + Real Vida Anual) × 0,4725% ÷ 12" : undefined}
              >
                <span className="flex items-center gap-1 min-w-0">
                  {topRank >= 0 && (
                    <span
                      className="text-[9px] font-bold rounded px-1 py-px bg-brand-600 text-white flex-shrink-0"
                      title={`#${topRank + 1} maior custo do mês`}
                      data-testid={`top-cost-badge-${topRank + 1}`}
                    >
                      #{topRank + 1}
                    </span>
                  )}
                  <span className="truncate">
                    {cost.name}
                    {isAcidentes && <span className="ml-1 text-[9px] text-brand-700 align-middle" title="Passe o rato para ver a fórmula">ⓘ</span>}
                  </span>
                </span>
                <span className="text-brand-700 font-mono flex-shrink-0">{formatCurrency(cost.value)}</span>
              </div>
            );
          });
        })()}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
        {(() => {
          const mc = getMarginColor(result.margin_percentage || 0, marginColors);
          return (
            <div className={`p-2 rounded text-center ${mc.danger ? 'border-2 border-red-500 animate-pulse' : ''}`} style={{ backgroundColor: mc.bg }} data-testid="margin-widget">
              <div className="text-[10px] flex items-center justify-center gap-1" style={{ color: mc.text }}>
                {mc.danger && <span aria-label="alerta">⚠️</span>}
                Margem
              </div>
              <div className="font-bold text-sm" style={{ color: mc.text }}>{formatCurrency(result.margin_amount)}</div>
              <div className="text-[10px]" style={{ color: mc.text, opacity: 0.9 }}>{result.margin_percentage?.toFixed(1)}%</div>
              {mc.danger && (
                <div className="text-[9px] font-bold mt-0.5" style={{ color: mc.text }}>NEGATIVA</div>
              )}
            </div>
          );
        })()}
        <div className={`p-2 rounded text-center ${fluidezColor}`}>
          <div className="text-[10px]">Fluidez</div>
          <div className="font-bold text-sm">{fluidezLabel}</div>
          <div className="text-[10px]">{paymentTerms} dias</div>
        </div>
      </div>
      <div className="pt-2 border-t border-slate-200">
        <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Resumo Anual</div>
        <div className="flex justify-between p-1 bg-emerald-50 rounded border border-emerald-200">
          <span className="text-emerald-700 flex items-center gap-1">Receita Anual <RevenueInfo result={result} formData={formData} /></span>
          <span className="font-bold text-emerald-700">{formatCurrency(annualRevenue)}</span>
        </div>
        <div className="flex justify-between p-1 bg-emerald-50 rounded border border-emerald-200">
          <span className="text-emerald-700">Custo Anual</span>
          <span className="text-emerald-700">{formatCurrency(annualCost)}</span>
        </div>
        <div className="flex justify-between p-1 bg-emerald-50 rounded border border-emerald-200 font-semibold">
          <span className="text-emerald-700">Margem Anual</span>
          <span className="text-emerald-700">{formatCurrency(annualMargin)}</span>
        </div>
      </div>
    </div>
  );
};

// ============ EMPLOYEE RESULTS ============
export const EmployeeResultsWidget = ({ result, formData }) => {
  const getDetailLabel = () => {
    if (formData?.business_type === "perm") return "Detalhe Candidato";
    if (formData?.regime_type === "green_receipts") return "Detalhe Recibos Verdes";
    if (formData?.regime_type === "b2b") return "Detalhe B2B";
    return "Detalhe Colaborador";
  };
  
  if (!result) {
    return (
      <div className="p-2 text-xs text-muted-foreground text-center py-4">Aguardando...</div>
    );
  }

  const regime = formData?.regime_type || "contract";
  const isPerm = formData?.business_type === "perm";
  const isFreelancer = regime === "green_receipts" || regime === "b2b";
  const grossMonthly = result.gross_monthly || 0;
  const netMonthly = result.net_monthly || 0;

  // ===== Vista Perm: detalhe candidato com benefícios escolhidos =====
  if (isPerm) {
    const bd = result.breakdown || {};
    const salaryMonths = bd.perm_salary_months || 14;
    const mealMonthly = bd.perm_meal_monthly || 0;
    const mealAnnualP = bd.perm_meal_annual || 0;
    const healthMonthly = bd.perm_health_monthly || 0;
    const healthAnnualP = bd.perm_health_annual || 0;
    const premioMonthly = bd.perm_premio_monthly || 0;
    const premioAnnualP = bd.perm_premio_annual || 0;
    const hasBenefits = mealMonthly > 0 || healthMonthly > 0 || premioMonthly > 0;
    return (
      <div className="p-2 text-xs space-y-1 overflow-y-auto max-h-[400px]">
        <div className="text-xs font-semibold text-brand-600 uppercase mb-1">Detalhe Candidato</div>
        <div className="flex justify-between p-1 bg-slate-50 rounded">
          <span>Salário Bruto ({salaryMonths} meses)</span>
          <span className="font-bold">{formatCurrency(grossMonthly)}</span>
        </div>
        <div className="flex justify-between p-1 bg-red-50 rounded">
          <span>IRS ({bd.perm_irs_rate ? bd.perm_irs_rate.toFixed(1) : 0}%)</span>
          <span className="text-red-600">-{formatCurrency(bd.perm_irs_monthly || 0)}</span>
        </div>
        <div className="flex justify-between p-1 bg-red-50 rounded">
          <span>SS (11%)</span>
          <span className="text-red-600">-{formatCurrency(bd.perm_ss_employee_monthly || 0)}</span>
        </div>
        <div className="flex justify-between p-1 bg-brand-100 rounded font-semibold mt-1">
          <span>Líquido Mensal</span>
          <span className="text-brand-700">{formatCurrency(bd.perm_net_monthly || 0)}</span>
        </div>
        {(() => {
          const grossM = bd.perm_gross_monthly || 0;
          const months = bd.perm_salary_months || 14;
          const netM = bd.perm_net_monthly || 0;
          // Líquido (14) = igual ao netM (já é a 14 meses). Líquido (12) = anualiza para 12 (duodécimos: +2 meses extra na média).
          const net14 = netM;
          const net12 = months > 12 ? (netM * months) / 12 : netM;
          return (
            <>
              <div className="flex justify-between p-1 bg-brand-50 rounded text-[10px]">
                <span>Líquido Mensal (12m — c/ duodécimos)</span>
                <span className="text-brand-700">{formatCurrency(net12)}</span>
              </div>
              <div className="flex justify-between p-1 bg-brand-50 rounded text-[10px]">
                <span>Líquido Mensal (14m — s/ duodécimos)</span>
                <span className="text-brand-700">{formatCurrency(net14)}</span>
              </div>
            </>
          );
        })()}
        {hasBenefits && (
          <div className="mt-2 pt-2 border-t border-pink-200">
            <div className="text-[10px] font-semibold text-pink-600 uppercase mb-1">+ Benefícios Selecionados</div>
            {mealMonthly > 0 && (
              <div className="flex justify-between p-1 bg-pink-50 rounded">
                <span>Sub. Refeição ({bd.perm_meal_months}m)</span>
                <span className="text-pink-600">+{formatCurrency(mealMonthly)}/mês</span>
              </div>
            )}
            {healthMonthly > 0 && (
              <div className="flex justify-between p-1 bg-pink-50 rounded">
                <span>Seguro Saúde ({bd.perm_health_months}m)</span>
                <span className="text-pink-600">+{formatCurrency(healthMonthly)}/mês</span>
              </div>
            )}
            {premioMonthly > 0 && (
              <div className="flex justify-between p-1 bg-pink-50 rounded">
                <span>Prémio ({bd.perm_premio_months}m)</span>
                <span className="text-pink-600">+{formatCurrency(premioMonthly)}/mês</span>
              </div>
            )}
            <div className="flex justify-between p-1 bg-pink-100 rounded font-semibold mt-1">
              <span>Total Benefícios Anuais</span>
              <span className="text-pink-700">{formatCurrency(mealAnnualP + healthAnnualP + premioAnnualP)}</span>
            </div>
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-slate-200">
          <div className="flex justify-between p-1 bg-slate-50 rounded">
            <span>Líquido Anual</span>
            <span className="font-bold">{formatCurrency(bd.perm_net_annual || 0)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Contrato: 14 meses | Recibos/B2B: usa payment_months do backend (11 ou 12)
  const annualMonths = isFreelancer ? (parseInt(formData?.payment_months || formData?.freelancer_billing_months) || 12) : 14;
  const grossAnnual = isFreelancer ? (Number(result.gross_annual) || grossMonthly * annualMonths) : grossMonthly * 14;
  const netAnnual = result.net_annual || (netMonthly * 12);
  
  // Benefícios só para Contrato
  const mealAllowance = isFreelancer ? 0 : (result.meal_allowance || result.breakdown?.meal_allowance || 0);
  const mealMonths = formData?.meal_allowance_months || 11;
  const healthInsurance = isFreelancer ? 0 : (formData?.health_insurance_cost || 0);
  const ticketInfancia = isFreelancer ? 0 : (formData?.ticket_infancia || 0);
  const ticketInfanciaMonths = formData?.ticket_infancia_months || 12;
  const ppr = isFreelancer ? 0 : (formData?.insurance_capitalization || 0);
  const pprMonths = formData?.insurance_capitalization_months || 12;
  const kmAllowance = isFreelancer ? 0 : (result.km_allowance || formData?.km_allowance || 0);
  const kmMonths = formData?.km_allowance_months || 11;
  // Sub. Alimentação: paga ao dia × mealMonths (default 11 — não é paga em Agosto)
  const mealAnnual = mealAllowance * mealMonths;
  const healthAnnual = healthInsurance * 12;
  const ticketAnnual = ticketInfancia * ticketInfanciaMonths;
  const pprAnnual = ppr * pprMonths;
  // Ajudas de Custo: pagas em kmMonths meses (default 11)
  const kmAnnual = kmAllowance * kmMonths;
  // Mensal DISTRIBUÍDO (mostrado no fluxo) — evita mostrar valor cheio quando na verdade só recebe em N meses
  const kmMonthlyDistributed = (kmAllowance * kmMonths) / 12;
  const totalBenefitsAnnual = mealAnnual + healthAnnual + ticketAnnual + pprAnnual + kmAnnual;
  const irsAnnual = (result.irs_retention || 0) * annualMonths;
  const ssAnnual = (result.social_security_employee || 0) * annualMonths;
  // Salário Líquido Mensal (14/12) — fonte de verdade = backend fields `liq_14_monthly` e `liq_12_monthly`
  // (expostos em 2026-02-25). Fórmula backend:
  //   liq_14 = bruto − IRS mensal − SS mensal + meal + km + Real Vida + ticket + coverflex (benefícios REAIS do mês)
  //   liq_12 = net_annual / 12 (duodécimos, inclui diluição dos subsídios de férias/natal)
  // Seguro Saúde é benefício em espécie — não entra em nenhum dos dois.
  const irsMonth = result.irs_retention || 0;
  const ssMonth = result.social_security_employee || 0;
  const subsidyNetMonthly = isFreelancer ? 0 : (grossMonthly - irsMonth - ssMonth);
  const backendNetMonthly = Number(result.net_monthly) || 0;
  const liq14Backend = Number(result.liq_14_monthly) || 0;
  const liq12Backend = Number(result.liq_12_monthly) || backendNetMonthly;
  // Fallback (simulações antigas sem liq_*_monthly): deriva por aproximação.
  const netWithDuodecimos = liq12Backend > 0 ? liq12Backend : backendNetMonthly;
  const netWithBenefitsMonthly = liq14Backend > 0
    ? liq14Backend
    : (netWithDuodecimos - (subsidyNetMonthly * 2) / 12);
  // Backend result.net_annual já inclui meal + km + ppr + ticket + coverflex.
  const netWithBenefitsAnnual = netAnnual;
  
  // IVA para Recibos Verdes
  const ivaMonthly = result.iva || 0;
  
  // SS label dinâmico
  const ssRate = result.breakdown?.ss_rate != null ? result.breakdown.ss_rate : 11;
  const ssExempt = result.breakdown?.ss_exempt || false;

  return (
    <div className="p-2 text-xs space-y-1 overflow-y-auto max-h-[400px]">
      <div className="text-xs font-semibold text-brand-600 uppercase mb-1">{getDetailLabel()}</div>
      <div className="flex justify-between p-1 bg-slate-100 rounded">
        <span>Salário Bruto</span>
        <span className="font-mono font-bold">{formatCurrency(grossMonthly)}</span>
      </div>
      {!isFreelancer && result.include_iht && (
        <>
          <div className="flex justify-between p-1 rounded text-[10px]">
            <span>Base (80%)</span>
            <span className="font-mono text-brand-700">{formatCurrency(result.iht_base)}</span>
          </div>
          <div className="flex justify-between p-1 rounded text-[10px]">
            <span>IHT (20%)</span>
            <span className="font-mono text-brand-700">{formatCurrency(result.iht_amount)}</span>
          </div>
        </>
      )}
      {(!isFreelancer || result.irs_retention > 0) && (() => {
        const grossM = result.gross_monthly || 0;
        const irsBase = Number(result.irs_base_monthly) || grossM;
        // Só mostra linha de "Base IRS" se diferir do bruto (ex: IHT isento de IRS → base = 80% do bruto).
        // Real Vida já não entra na base (decisão de negócio 2026-02-25).
        const showBase = !isFreelancer && Math.abs(irsBase - grossM) > 0.01;
        return (
          <>
            {showBase && (
              <div
                className="flex justify-between p-1 rounded text-[10px] bg-amber-50 border border-amber-100"
                title="Base IRS reduzida: o IHT (20% do bruto) está isento de IRS por configuração do admin. IRS aplicado apenas sobre a Base (80%)."
                data-testid="irs-base-row"
              >
                <span className="text-amber-800">Base IRS (sem IHT) ⓘ</span>
                <span className="font-mono text-amber-900">{formatCurrency(irsBase)}</span>
              </div>
            )}
            <div className="flex justify-between p-1 rounded">
              <span>IRS ({irsBase > 0 ? ((result.irs_retention / irsBase) * 100).toFixed(1) : 0}%)</span>
              <span className="font-mono text-red-600">-{formatCurrency(result.irs_retention)}</span>
            </div>
          </>
        );
      })()}
      {!(isFreelancer && ssExempt) && (
        <div className="flex justify-between p-1 rounded">
          <span>{`SS (${ssRate}%${isFreelancer ? " s/ 70%" : ""})`}</span>
          <span className="font-mono text-red-600">-{formatCurrency(result.social_security_employee)}</span>
        </div>
      )}
      {isFreelancer && ssExempt && (
        <div className="flex justify-between p-1 rounded">
          <span className="text-brand-700 text-[10px]">1.º ano: SS isento</span>
        </div>
      )}
      {isFreelancer && (() => {
        // billMonths reflecte quantos meses o freelancer é PAGO — fonte da verdade
        // é o backend (`result.gross_annual`), que já usa `payment_months` para
        // multiplicar. Só usamos `formData` como fallback caso o backend não devolva.
        const billMonths = parseInt(formData?.payment_months || formData?.freelancer_billing_months || 11);
        const isB2B = (formData?.regime_type === "b2b");
        const isRecibos = (formData?.regime_type === "green_receipts");
        const includeHealth = !!formData?.include_health_insurance;
        const healthCost = includeHealth ? parseFloat(formData?.health_insurance_cost || 0) : 0;
        const valorBrutoMensal = grossMonthly + healthCost;
        // USAR result.gross_annual do backend em vez de recomputar localmente,
        // para garantir que o toggle 11/12 é reflectido correctamente na UI.
        const backendGrossAnnual = Number(result?.gross_annual) || 0;
        const valorBrutoAnual = backendGrossAnnual > 0
          ? backendGrossAnnual + (healthCost * 12)
          : valorBrutoMensal * billMonths;
        return (
          <div className="mt-2 pt-1 border-t border-brand-200">
            {healthCost > 0 && (
              <div className="flex justify-between p-1 bg-brand-50 rounded mt-1 text-[10px]">
                <span>+ Seguro de Saúde</span>
                <span className="text-brand-700">+{formatCurrency(healthCost)}</span>
              </div>
            )}
            <div className="flex justify-between p-1 bg-[#FADCBC] rounded font-bold mt-1 text-[var(--brand-primary)]" data-testid="freelancer-valor-bruto-mensal">
              <span>Valor Bruto Mensal</span>
              <span>{formatCurrency(valorBrutoMensal)}</span>
            </div>
            {/* Líquido Mensal — só Recibos Verdes */}
            {isRecibos && (
              <div className="flex justify-between p-1 bg-[#FADCBC] rounded font-bold mt-1 text-[var(--brand-primary)]">
                <span>Valor Líquido Mensal</span>
                <span className="font-mono">{formatCurrency(netMonthly)}</span>
              </div>
            )}
            {/* Bruto Anual — apenas Recibos (B2B não tem) */}
            {!isB2B && (
              <div className="flex justify-between p-1 bg-[#FADCBC] rounded font-bold mt-1 text-[var(--brand-primary)]" data-testid="freelancer-valor-bruto-anual">
                <span>Valor Bruto Anual ({billMonths} m)</span>
                <span>{formatCurrency(valorBrutoAnual)}</span>
              </div>
            )}
          </div>
        );
      })()}
      {!isFreelancer && (ticketInfancia > 0 || ppr > 0 || mealAllowance > 0 || kmAllowance > 0) && (() => {
        // Sub. Refeição e Seguro Saúde excluídos da % Benefícios por decisão
        // de negócio — só entram KM + Real Vida (PPR) + Ticket Infância.
        const totalBenefits = (ticketInfancia || 0) + (ppr || 0) + (kmAllowance || 0);
        const grossMonthly = result.gross_monthly || 0;
        const benefitsPct = grossMonthly > 0 ? (totalBenefits / grossMonthly) * 100 : 0;
        const overLimit = benefitsPct > 35;
        return (
        <div className="mt-2 pt-1 border-t border-brand-100">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-semibold text-brand-600 uppercase">+ Benefícios Mensais</div>
          </div>
          {mealAllowance > 0 && (
            <div className="flex justify-between p-1 rounded">
              <span>Sub. Refeição</span>
              <span className="text-brand-700">+{formatCurrency(mealAllowance)}</span>
            </div>
          )}
          {ticketInfancia > 0 && (
            <div className="flex justify-between p-1 rounded">
              <span>Ticket Infância</span>
              <span className="text-brand-700">+{formatCurrency(ticketInfancia)}</span>
            </div>
          )}
          {ppr > 0 && (
            <div className="flex justify-between p-1 rounded">
              <span>Real Vida</span>
              <span className="text-brand-700">+{formatCurrency(ppr)}</span>
            </div>
          )}
          {/* Seguro Saúde omitido do líquido por decisão de negócio (2026-02-25):
              é benefício em espécie, não cash. Continua a entrar em custo empresa. */}
          {kmAllowance > 0 && (
            <div className="flex justify-between p-1 rounded">
              <span>Deslocação (KM)</span>
              <span className="text-brand-700">+{formatCurrency(kmMonthlyDistributed)}</span>
            </div>
          )}
          <div className="flex justify-between p-1 bg-slate-100 rounded font-semibold mt-1">
            <span>Salário Líquido Mensal (14)</span>
            <span className="text-brand-700">{formatCurrency(netWithBenefitsMonthly)}</span>
          </div>
        </div>
        );
      })()}
      {!isFreelancer && (
        <div className="mt-2 pt-1 border-t border-brand-100">
          <div className="flex justify-between p-1 bg-slate-100 rounded">
            <span>Salário Líquido Mensal (12)</span>
            <span className="font-bold text-brand-700">{formatCurrency(netWithDuodecimos)}</span>
          </div>
        </div>
      )}
      <div className="mt-2 pt-1 border-t border-slate-200">
        <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Resumo Anual</div>
        <div className="flex justify-between p-1 bg-slate-100 rounded">
          <span>Bruto Anual ({annualMonths}m)</span>
          <span className="font-bold">{formatCurrency(grossAnnual)}</span>
        </div>
        <div className="flex justify-between p-1 rounded">
          <span>IRS Anual</span>
          <span className="text-red-600">-{formatCurrency(irsAnnual)}</span>
        </div>
        <div className="flex justify-between p-1 rounded">
          <span>SS Anual</span>
          <span className="text-red-600">-{formatCurrency(ssAnnual)}</span>
        </div>
        {!isFreelancer && totalBenefitsAnnual > 0 && (
          <>
            <div className="text-[10px] font-semibold text-brand-600 uppercase mt-1">Benefícios Anuais</div>
            {mealAnnual > 0 && (
              <div className="flex justify-between p-1 rounded">
                <span>Sub. Refeição ({mealMonths}m)</span>
                <span className="text-brand-700">+{formatCurrency(mealAnnual)}</span>
              </div>
            )}
            {/* Seguro Saúde intencionalmente omitido da Vista Anual (visual apenas) */}
            {ticketAnnual > 0 && (
              <div className="flex justify-between p-1 rounded">
                <span>T. Infância ({ticketInfanciaMonths}m)</span>
                <span className="text-brand-700">+{formatCurrency(ticketAnnual)}</span>
              </div>
            )}
            {pprAnnual > 0 && (
              <div className="flex justify-between p-1 rounded">
                <span>Real Vida ({pprMonths}m)</span>
                <span className="text-brand-700">+{formatCurrency(pprAnnual)}</span>
              </div>
            )}
            {kmAnnual > 0 && (
              <div className="flex justify-between p-1 rounded">
                <span>Deslocação ({kmMonths}m)</span>
                <span className="text-brand-700">+{formatCurrency(kmAnnual)}</span>
              </div>
            )}
          </>
        )}
        <div className="flex justify-between p-1 bg-slate-100 rounded font-semibold mt-1">
          <span>Líquido Anual Total</span>
          <span className="text-brand-700">{formatCurrency(netWithBenefitsAnnual)}</span>
        </div>
      </div>
    </div>
  );
};

// ============ PREMIUM ============
export const PremiumWidget = ({ results }) => {
  if (!results?.business_decision?.premium?.premium_value) {
    return (
      <div className="p-3 h-full bg-slate-100 rounded-lg flex items-center justify-center text-sm text-muted-foreground">
        Sem prémio
      </div>
    );
  }
  
  return (
    <div className="p-3 h-full bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg">
      <div className="flex items-center gap-2">
        <Trophy className="w-6 h-6 text-amber-500" />
        <div>
          <div className="text-xs text-amber-600">Prémio</div>
          <div className="font-bold text-amber-800">{results.business_decision.premium.tier}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="font-mono font-bold text-lg text-amber-700">{formatCurrency(results.business_decision.premium.premium_value)}</div>
        </div>
      </div>
    </div>
  );
};

// ============ ANNUAL TABLE ============
export const AnnualTableWidget = ({ result }) => (
  <div className="p-2 text-[10px]">
    <div className="text-xs font-semibold text-brand-600 uppercase mb-1">Simulação Anual</div>
    <div className="border rounded overflow-hidden">
      <div className="grid grid-cols-3 bg-slate-100 font-semibold">
        <div className="p-1 border-r">Item</div>
        <div className="p-1 border-r text-right">Mensal</div>
        <div className="p-1 text-right">Anual</div>
      </div>
      <div className="grid grid-cols-3 border-t">
        <div className="p-1 border-r">Bruto</div>
        <div className="p-1 border-r text-right font-mono">{result ? formatCurrency(result.gross_monthly) : '-'}</div>
        <div className="p-1 text-right font-mono">{result ? formatCurrency(result.gross_monthly * 14) : '-'}</div>
      </div>
    </div>
  </div>
);
